import { Markdown } from '@/src/components/common/Markdown';
import MarkDownPreviewEditor from '@/src/components/common/MarkdownPreviewEditor/MarkDownPreviewEditor';
import useInputBuffer from '@/src/hooks/useInputBuffer';
import { Container, FormField, Header, Textarea } from '@amzn/awsui-components-react';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChallengeLearningOutcomeFields,
  ChallengePropAction,
  LEARNING_OUTCOME_CONTENT_MAX_LENGTH,
  SUMMARY_MAX_LENGTH,
  SUMMARY_MIN_LENGTH,
  useCreateChallenge,
} from '../../../../store/create-challenge.context';
import { Challenge, ChallengeLearningOutcome, ChallengeReviewableSection } from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { HorizontalRule } from '../../../common/HorizontalRule';
import { KeyValue } from '../../../common/KeyValue';
import { ChallengeReviewPopover } from '../../challengesCommon/ChallengeReviewPopover';

interface LearningOutcomeDetailProps {
  challenge: Challenge;
}

const LearningOutcomeDetail: React.FC<LearningOutcomeDetailProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const { editMode, editedChallenge, handleUpdateChallengeProp, challengeLearningOutcomeValidator } =
    useCreateChallenge();
  // Keeps the content char-count live while the model commit stays debounced.
  // undefined = not editing (counter falls back to the committed model).
  const [contentBuffer, setContentBuffer] = useState<string | undefined>(undefined);

  // error texts and validation logic
  const [summaryErrorText, setSummaryErrorText] = useState<string>('');
  const [contentErrorText, setContentErrorText] = useState<string>('');
  const field = i18nKeys.challenges.subSections.learningPlan.fields;

  useEffect(() => {
    // reset error fields when editMode is changed
    if (!editMode) {
      setSummaryErrorText('');
      setContentErrorText('');
      setContentBuffer(undefined);
    }
  }, [editMode]);

  const validator = challengeLearningOutcomeValidator(
    new Map<ChallengeLearningOutcomeFields, (error: string) => void>([
      [ChallengeLearningOutcomeFields.SUMMARY, (error: string) => setSummaryErrorText(error)],
      [ChallengeLearningOutcomeFields.CONTENT, (error: string) => setContentErrorText(error)],
    ])
  );

  const handleLearningOutcome = (modifiedLearningOutcome: ChallengeLearningOutcome) => {
    handleUpdateChallengeProp(ChallengePropAction.LEARNING_OUTCOME, modifiedLearningOutcome);
  };

  interface LearningOutcomeHelper {
    attribute: ChallengeLearningOutcomeFields;
    value: string;
  }

  const handleInputChange = (event: LearningOutcomeHelper) => {
    const { attribute, value } = event;
    const learningOutcome: ChallengeLearningOutcome =
      cloneDeep(editedChallenge?.props.learningOutcome) || new ChallengeLearningOutcome();
    switch (attribute) {
      case ChallengeLearningOutcomeFields.SUMMARY:
        Object.assign(learningOutcome, { summary: value });
        break;
      case ChallengeLearningOutcomeFields.INTRODUCTION:
        Object.assign(learningOutcome, { introduction: value });
        break;
      case ChallengeLearningOutcomeFields.TOPICS_COVERED:
        Object.assign(learningOutcome, { topicsCovered: value });
        break;
      case ChallengeLearningOutcomeFields.PRE_REQUISITES:
        Object.assign(learningOutcome, { technicalKnowledgePrerequisites: value });
        break;
      case ChallengeLearningOutcomeFields.CONTENT:
        Object.assign(learningOutcome, { content: value });
        break;
      default:
        throw new Error('Unrecognized field on ChallengeLearningOutcome');
    }
    handleLearningOutcome(learningOutcome);
  };

  const contentConstraintProps = useMemo(() => {
    // Count the live buffer while editing; fall back to the committed model.
    const content = contentBuffer ?? editedChallenge?.props?.learningOutcome?.content ?? '';
    const count = content.length;
    const msg = `${t(i18nKeys.challenges.subSections.learningPlan.fields.content.constraint, {
      count,
      maxLength: LEARNING_OUTCOME_CONTENT_MAX_LENGTH,
    })}. ${t(i18nKeys.general.avoidPIIWarning)}`;
    if (count > LEARNING_OUTCOME_CONTENT_MAX_LENGTH) {
      return { errorText: msg };
    }
    return { constraintText: msg };
  }, [contentBuffer, editedChallenge?.props?.learningOutcome?.content]);

  // Buffer the summary while editing and commit it to the model on blur.
  const summaryInput = useInputBuffer(editedChallenge?.props?.learningOutcome?.summary ?? '', (value) =>
    handleInputChange({ attribute: ChallengeLearningOutcomeFields.SUMMARY, value })
  );

  useEffect(() => {
    // Validate after the async blur commit lands, so we never read a stale value.
    if (editMode) {
      validator.isValidField(ChallengeLearningOutcomeFields.SUMMARY);
    }
  }, [editedChallenge?.props?.learningOutcome?.summary]);

  return (
    <Container
      header={
        <Header variant="h2" actions={<ChallengeReviewPopover section={ChallengeReviewableSection.LEARNING_OUTCOME} />}>
          {t(i18nKeys.challenges.challengeDetails.headings.learningPlan)}
        </Header>
      }
    >
      <KeyValue
        alignWithDescriptiveKeyValues
        className="primary-text"
        label={<Header>{t(i18nKeys.challenges.challengeDetails.titles.summary)}</Header>}
      >
        {!editMode ? (
          challenge.props?.learningOutcome?.summary
        ) : (
          <FormField
            errorText={summaryErrorText}
            constraintText={`${t(field.summary.constraint, { SUMMARY_MIN_LENGTH, SUMMARY_MAX_LENGTH })} ${t(
              i18nKeys.general.avoidPIIWarning
            )}`}
          >
            <Textarea
              onChange={({ detail }) => summaryInput.handleChange(detail.value)}
              name="learningOutcomeSummary"
              value={summaryInput.value}
              placeholder={t(i18nKeys.challenges.subSections.learningPlan.fields.summary.placeholder)}
              onBlur={summaryInput.handleBlur}
              data-testid="learningoutcome-summary-input"
            />
          </FormField>
        )}
      </KeyValue>
      <HorizontalRule />

      {!editMode ? (
        <KeyValue
          alignWithDescriptiveKeyValues
          label={<Header>{t(i18nKeys.challenges.subSections.learningPlan.fields.content.title)}</Header>}
        >
          <Markdown content={challenge.props.learningOutcome.content || ''} allowHtml />
        </KeyValue>
      ) : (
        <FormField
          label={t(i18nKeys.challenges.subSections.learningPlan.fields.content.title)}
          stretch
          {...contentConstraintProps}
        >
          <MarkDownPreviewEditor
            text={challenge.props.learningOutcome.content || ''}
            onTextChanged={(text) => {
              setContentBuffer(text);
              handleInputChange({ attribute: ChallengeLearningOutcomeFields.CONTENT, value: text });
              validator.isValidField(ChallengeLearningOutcomeFields.CONTENT);
            }}
          />
        </FormField>
      )}
    </Container>
  );
};

export default LearningOutcomeDetail;
